// src/contexts/PlanContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { fetchPlanDetails } from '../utils/planConstants';

// Create context
export const PlanContext = createContext(null);

export const PlanProvider = ({ children }) => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plans from API on initial load
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const planData = await fetchPlanDetails();
        
        // Check if we got valid plan data
        if (Object.keys(planData).length === 0) {
          setError('No plan data available');
        } else {
          setPlans(planData);
        }
      } catch (err) {
        console.error('Error loading plans:', err);
        setError('Failed to load plan information');
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  // Create a memoized value for the context
  const value = React.useMemo(() => ({
    plans,
    loading,
    error,
    refreshPlans: async () => {
      try {
        setLoading(true);
        setError(null);
        const planData = await fetchPlanDetails();
        
        if (Object.keys(planData).length === 0) {
          setError('No plan data available');
          return false;
        }
        
        setPlans(planData);
        return true;
      } catch (err) {
        console.error('Error refreshing plans:', err);
        setError('Failed to refresh plan information');
        return false;
      } finally {
        setLoading(false);
      }
    }
  }), [plans, loading, error]);

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

// Custom hook to use the plan context
export const usePlans = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlans must be used within a PlanProvider');
  }
  return context;
};

export default PlanProvider; 